import { useState, useEffect, useCallback } from "react";
import axiosInstance from "../axiosInstance";

/**
 * Support Packages API (Support spec v1.0 §2).
 *
 * Replaces the older `/system-settings/support-levels` endpoints, which carried a single price
 * per level and a free-text benefits list. The spec splits price and default PER PRODUCT (§3.1)
 * and replaces benefits with a central service catalogue assigned per level (§4, §5), so the
 * payload shape is different enough that the old hooks could not be reused.
 *
 * §2 allows exactly two resources here: levels and services. Pricing and service fees live
 * inside those, and neither gets an endpoint of its own.
 */
const BASE = "/support-packages";

const unwrap = (res) => res.data?.data ?? res.data;
const errText = (err, fallback) =>
  err?.response?.data?.message ||
  err?.response?.data?.errors?.[0]?.message ||
  (err?.request ? "Cannot reach server. Check that the backend is running." : null) ||
  err?.message ||
  fallback;

/* ─────────────────────────── Support Levels ─────────────────────────── */

export const getSupportLevels = (params = {}) =>
  axiosInstance.get(`${BASE}/levels`, { params }).then(unwrap);
export const getSupportLevel = (id) => axiosInstance.get(`${BASE}/levels/${id}`).then(unwrap);
export const createSupportLevel = (data) => axiosInstance.post(`${BASE}/levels`, data).then(unwrap);
export const updateSupportLevel = (id, data) => axiosInstance.put(`${BASE}/levels/${id}`, data).then(unwrap);
export const deleteSupportLevel = (id) => axiosInstance.delete(`${BASE}/levels/${id}`).then(unwrap);
export const reorderSupportLevels = (ids) => axiosInstance.put(`${BASE}/levels/reorder`, { ids }).then(unwrap);

/* ─────────────────────────── Support Services ─────────────────────────── */

export const getSupportServices = (params = {}) =>
  axiosInstance.get(`${BASE}/services`, { params }).then(unwrap);
export const getSupportService = (id) => axiosInstance.get(`${BASE}/services/${id}`).then(unwrap);
export const createSupportService = (data) => axiosInstance.post(`${BASE}/services`, data).then(unwrap);
export const updateSupportService = (id, data) => axiosInstance.put(`${BASE}/services/${id}`, data).then(unwrap);
export const deleteSupportService = (id) => axiosInstance.delete(`${BASE}/services/${id}`).then(unwrap);

/* ─────────────────────────── list hooks ─────────────────────────── */

/**
 * One list hook for both resources, because the two overviews (§6.1, §6.3) differ only in their
 * filters. `deps` is spread into the effect so a filter change refetches without the caller
 * having to remember to.
 */
function useList(fetcher, params) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const key = JSON.stringify(params);
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetcher(JSON.parse(key));
      setItems(data?.items ?? []);
      setTotal(data?.total ?? 0);
    } catch (err) {
      setError(errText(err, "Failed to load"));
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
    // `fetcher` is a module-level function and `key` is the serialised params, so this is the
    // whole dependency set; passing the object itself would refetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    load();
  }, [load]);

  return { items, total, loading, error, refetch: load };
}

export const useSupportLevelsList = (params = {}) => useList(getSupportLevels, params);
export const useSupportServicesList = (params = {}) => useList(getSupportServices, params);

/** Every active service, for the assignment picker inside a level (§5). */
export const useActiveSupportServices = () => {
  const [services, setServices] = useState([]);
  useEffect(() => {
    let cancelled = false;
    getSupportServices({ status: "active", limit: 200 })
      .then((d) => {
        if (!cancelled) setServices(d?.items ?? []);
      })
      .catch(() => {
        if (!cancelled) setServices([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return services;
};

/* ─────────────────────────── mutation hook ─────────────────────────── */

/**
 * A single mutation wrapper. Returns the server's message on failure rather than a generic one,
 * because this module's 409s and 422s say something the operator needs to read: "this level has
 * been used on a booking", "the same service is assigned twice for FLIGHT_ONLY".
 */
export function useMutation(fn) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(
    async (...args) => {
      setLoading(true);
      setError(null);
      try {
        return await fn(...args);
      } catch (err) {
        const msg = errText(err, "Request failed");
        setError(msg);
        const wrapped = new Error(msg);
        wrapped.response = err?.response;
        throw wrapped;
      } finally {
        setLoading(false);
      }
    },
    [fn]
  );

  return { execute, loading, error };
}
